import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  CloudUpload,
  DatabaseBackup,
  ExternalLink,
  FolderOpen,
  HardDrive,
  KeyRound,
  Link2,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  StatTile,
  cn,
  type Column,
} from '../components/ui';
import { api, compact } from '../lib/api';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
} from '../lib/format';
import { useT } from '../lib/i18n';
import { errorMessage, useToast } from '../lib/toast';
import type {
  BackupRun,
  BackupRunStatus,
  BackupStatus,
  Paginated,
} from '../lib/types';

const STATUS_KEY = ['backups', 'status'] as const;
/** While a backup runs, the page follows it at this pace. */
const POLL_MS = 3_000;

const RUN_TONES: Record<BackupRunStatus, 'brand' | 'good' | 'danger'> = {
  running: 'brand',
  succeeded: 'good',
  failed: 'danger',
};

/**
 * Database backups to Google Drive.
 *
 * Three questions, top to bottom: are we backed up (the tiles), where do the
 * backups go and how often (connection and schedule), and what happened each
 * time (the history). A backup runs on the server in the background; this
 * page only polls while one is in progress.
 */
export function BackupsPage() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api.get<BackupStatus>('/api/backups/status'),
    refetchInterval: (query) => (query.state.data?.running ? POLL_MS : false),
  });

  // When a run ends, the history and the tiles both changed.
  const runningId = status.data?.running?.id ?? null;
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['backups', 'runs'] });
  }, [runningId, queryClient]);

  const start = useMutation({
    mutationFn: () => api.post<BackupRun>('/api/backups/runs'),
    onSuccess: () => {
      toast.success(t('backups.toast.started'));
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const data = status.data;
  const connected = data?.drive.status === 'active';

  return (
    <>
      <PageHeader
        title={t('backups.title')}
        description={t('backups.description')}
        action={
          <Button
            variant="primary"
            icon={<CloudUpload size={16} />}
            loading={start.isPending || Boolean(data?.running)}
            disabled={!connected || !data?.configured}
            onClick={() => start.mutate()}
          >
            {data?.running ? t('backups.action.running') : t('backups.action.backupNow')}
          </Button>
        }
      />

      {status.isError && <ErrorNote error={status.error} />}

      {status.isPending || !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : (
        <div className="space-y-4">
          <Tiles status={data} />
          {data.running && <RunningBanner run={data.running} />}

          <div className="grid gap-4 lg:grid-cols-5">
            <DriveCard status={data} className="lg:col-span-3" />
            <ScheduleCard status={data} className="lg:col-span-2" />
          </div>

          <History />
        </div>
      )}
    </>
  );
}

// ── Tiles ───────────────────────────────────────────────────────────────────

function Tiles({ status }: { status: BackupStatus }) {
  const t = useT();
  const last = status.lastSucceeded;
  const connected = status.drive.status === 'active';

  // Stale means more than two intervals since the last good backup — one
  // missed run can be a blip, two is a pattern worth a colour.
  const staleAfterMs = status.settings.intervalHours * 2 * 60 * 60_000;
  const lastTone = !last
    ? connected
      ? 'danger'
      : 'neutral'
    : Date.now() - new Date(last.startedAt).getTime() > staleAfterMs
      ? 'warn'
      : 'good';

  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <StatTile
        tone={lastTone}
        icon={<ShieldCheck size={14} />}
        label={t('backups.tile.last')}
        value={last ? formatRelative(last.startedAt) : t('common.never')}
        caption={last ? formatBytes(last.sizeBytes) : t('backups.tile.lastNone')}
      />
      <StatTile
        icon={<CalendarClock size={14} />}
        label={t('backups.tile.next')}
        value={
          status.nextRunAt
            ? formatRelative(status.nextRunAt)
            : status.running
              ? t('backups.tile.nextRunning')
              : t('backups.tile.nextNone')
        }
        caption={
          status.settings.enabled
            ? intervalLabel(t, status.settings.intervalHours)
            : t('backups.tile.paused')
        }
      />
      <StatTile
        tone="brand"
        icon={<HardDrive size={14} />}
        label={t('backups.tile.stored')}
        value={formatNumber(status.stored.count)}
        caption={formatBytes(status.stored.bytes)}
      />
      <StatTile
        tone={status.failuresLast7Days > 0 ? 'danger' : 'neutral'}
        icon={<AlertTriangle size={14} />}
        label={t('backups.tile.failures')}
        value={formatNumber(status.failuresLast7Days)}
        caption={t('backups.tile.failuresCaption')}
      />
    </div>
  );
}

function RunningBanner({ run }: { run: BackupRun }) {
  const t = useT();

  return (
    <div className="flex items-start gap-3 rounded-xl border border-brand/25 bg-brand-soft px-4 py-3">
      <Loader2 size={18} className="mt-0.5 shrink-0 animate-spin text-brand" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink">{t('backups.running.title')}</p>
        <p className="mt-0.5 text-xs text-ink-2 break-anywhere">
          {t('backups.running.detail', {
            file: run.fileName,
            since: formatRelative(run.startedAt),
          })}
        </p>
      </div>
    </div>
  );
}

// ── Drive connection ────────────────────────────────────────────────────────

function DriveCard({
  status,
  className,
}: {
  status: BackupStatus;
  className?: string;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const drive = status.drive;

  const connect = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/backups/drive/connect-url'),
    // Off to Google's consent screen; it sends the browser back to
    // /backups/drive/callback, which finishes the connection.
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const disconnect = useMutation({
    mutationFn: (reason: string) =>
      api.post<BackupStatus>('/api/backups/drive/disconnect', { reason }),
    onSuccess: (next) => {
      queryClient.setQueryData(STATUS_KEY, next);
      setConfirmDisconnect(false);
      toast.success(t('backups.toast.disconnected'));
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card className={className}>
      <CardHeader
        title={t('backups.drive.title')}
        description={t('backups.drive.description')}
      />
      <div className="space-y-4 p-4 sm:p-5">
        {!status.configured ? (
          <Notice tone="warn" icon={<AlertTriangle size={18} />} title={t('backups.drive.notConfigured')}>
            {t('backups.drive.notConfiguredDetail')}
            <code className="mt-2 block font-mono text-[11px] break-anywhere">
              BACKUP_GOOGLE_CLIENT_ID · BACKUP_GOOGLE_CLIENT_SECRET ·
              BACKUP_GOOGLE_REDIRECT_URI · PAYMENTS_ENCRYPTION_KEY
            </code>
          </Notice>
        ) : drive.status === 'active' ? (
          <>
            <Notice tone="good" icon={<CheckCircle2 size={18} />} title={t('backups.drive.connected')}>
              {t('backups.drive.connectedDetail')}
            </Notice>
            <dl className="divide-y divide-line">
              <Row label={t('backups.drive.account')}>
                <span className="break-anywhere">
                  {drive.accountName ? `${drive.accountName} · ` : ''}
                  {drive.accountEmail}
                </span>
              </Row>
              <Row label={t('backups.drive.folder')}>
                <span className="break-anywhere">{drive.folderName ?? '—'}</span>
              </Row>
              <Row label={t('backups.drive.connectedAt')}>
                {formatDateTime(drive.connectedAt)}
                {drive.connectedBy ? ` · ${drive.connectedBy}` : ''}
              </Row>
            </dl>
            <div className="flex flex-wrap gap-2">
              {drive.folderUrl && (
                <a
                  href={drive.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[38px] items-center gap-2 rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
                >
                  <FolderOpen size={16} />
                  {t('backups.drive.openFolder')}
                </a>
              )}
              <Button
                icon={<RefreshCw size={16} />}
                loading={connect.isPending}
                onClick={() => connect.mutate()}
              >
                {t('backups.drive.switch')}
              </Button>
              <Button
                variant="danger"
                icon={<Unlink size={16} />}
                onClick={() => setConfirmDisconnect(true)}
              >
                {t('backups.drive.disconnect')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {drive.status === 'revoked' ? (
              <Notice tone="danger" icon={<AlertTriangle size={18} />} title={t('backups.drive.revoked')}>
                {t('backups.drive.revokedDetail', { account: drive.accountEmail ?? '—' })}
                {drive.lastError && (
                  <span className="mt-1 block text-xs">{drive.lastError}</span>
                )}
              </Notice>
            ) : (
              <Notice tone="neutral" icon={<DatabaseBackup size={18} />} title={t('backups.drive.none')}>
                {t('backups.drive.noneDetail')}
              </Notice>
            )}
            <ul className="space-y-2 text-sm text-ink-2">
              <Point icon={<ShieldCheck size={15} />}>{t('backups.drive.pointScope')}</Point>
              <Point icon={<FolderOpen size={15} />}>{t('backups.drive.pointFolder')}</Point>
              <Point icon={<KeyRound size={15} />}>{t('backups.drive.pointToken')}</Point>
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                icon={<Link2 size={16} />}
                loading={connect.isPending}
                onClick={() => connect.mutate()}
              >
                {drive.status === 'revoked'
                  ? t('backups.drive.reconnect')
                  : t('backups.drive.connect')}
              </Button>
              {drive.status === 'revoked' && (
                <Button
                  variant="ghost"
                  icon={<Unlink size={16} />}
                  onClick={() => setConfirmDisconnect(true)}
                >
                  {t('backups.drive.forget')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDisconnect}
        destructive
        title={t('backups.drive.disconnectTitle')}
        description={t('backups.drive.disconnectDetail')}
        confirmLabel={t('backups.drive.disconnect')}
        loading={disconnect.isPending}
        onConfirm={(reason) => disconnect.mutate(reason)}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </Card>
  );
}

function Notice({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'neutral' | 'good' | 'warn' | 'danger';
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const tones = {
    neutral: 'border-line bg-raised text-ink-2',
    good: 'border-good/25 bg-good-soft text-good',
    warn: 'border-warn/30 bg-warn-soft text-warn',
    danger: 'border-danger/30 bg-danger-soft text-danger',
  } as const;

  return (
    <div className={cn('flex items-start gap-3 rounded-lg border px-3.5 py-3', tones[tone])}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <div className="mt-0.5 text-xs text-ink-2">{children}</div>
      </div>
    </div>
  );
}

function Point({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0 text-brand">{icon}</span>
      <span>{children}</span>
    </li>
  );
}

// ── Schedule ────────────────────────────────────────────────────────────────

interface ScheduleForm {
  enabled: boolean;
  intervalHours: number;
  retentionCount: string;
}

function ScheduleCard({
  status,
  className,
}: {
  status: BackupStatus;
  className?: string;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const saved = status.settings;

  const initial = useMemo<ScheduleForm>(
    () => ({
      enabled: saved.enabled,
      intervalHours: saved.intervalHours,
      retentionCount: String(saved.retentionCount),
    }),
    [saved.enabled, saved.intervalHours, saved.retentionCount],
  );
  const [form, setForm] = useState<ScheduleForm>(initial);
  const [confirming, setConfirming] = useState(false);

  // A save (or a colleague's) changes what is stored; follow it.
  useEffect(() => setForm(initial), [initial]);

  const retention = Number(form.retentionCount);
  const retentionValid =
    Number.isInteger(retention) &&
    retention >= saved.minRetention &&
    retention <= saved.maxRetention;
  const dirty =
    form.enabled !== saved.enabled ||
    form.intervalHours !== saved.intervalHours ||
    retention !== saved.retentionCount;

  const save = useMutation({
    mutationFn: (reason: string) =>
      api.patch<BackupStatus>(
        '/api/backups/settings',
        compact({
          enabled: form.enabled !== saved.enabled ? form.enabled : undefined,
          intervalHours:
            form.intervalHours !== saved.intervalHours ? form.intervalHours : undefined,
          retentionCount: retention !== saved.retentionCount ? retention : undefined,
          reason,
        }),
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(STATUS_KEY, next);
      setConfirming(false);
      toast.success(t('common.done'));
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const historyDays = retentionValid
    ? Math.max(1, Math.round((retention * form.intervalHours) / 24))
    : null;

  return (
    <Card className={className}>
      <CardHeader
        title={t('backups.schedule.title')}
        description={t('backups.schedule.description')}
      />
      <div className="space-y-4 p-4 sm:p-5">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            className="mt-0.5 size-4 accent-brand"
          />
          <span>
            <span className="block text-sm font-medium text-ink">
              {t('backups.schedule.enabled')}
            </span>
            <span className="block text-xs text-ink-2">
              {t('backups.schedule.enabledHint')}
            </span>
          </span>
        </label>

        <Field label={t('backups.schedule.interval')}>
          <Select
            value={form.intervalHours}
            disabled={!form.enabled}
            onChange={(event) =>
              setForm({ ...form, intervalHours: Number(event.target.value) })
            }
          >
            {saved.intervalOptions.map((hours) => (
              <option key={hours} value={hours}>
                {intervalLabel(t, hours)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('backups.schedule.retention')}
          hint={
            historyDays === null
              ? t('backups.schedule.retentionRange', {
                  min: saved.minRetention,
                  max: saved.maxRetention,
                })
              : t('backups.schedule.retentionHint', { days: historyDays })
          }
        >
          <Input
            type="number"
            inputMode="numeric"
            min={saved.minRetention}
            max={saved.maxRetention}
            value={form.retentionCount}
            onChange={(event) =>
              setForm({ ...form, retentionCount: event.target.value })
            }
          />
        </Field>

        <div className="flex flex-wrap justify-end gap-2">
          {dirty && (
            <Button variant="ghost" onClick={() => setForm(initial)}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            variant="primary"
            disabled={!dirty || !retentionValid}
            onClick={() => setConfirming(true)}
          >
            {t('common.saveChanges')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('common.saveTheseChanges')}
        description={t('backups.schedule.confirmDetail')}
        confirmLabel={t('common.save')}
        loading={save.isPending}
        onConfirm={(reason) => save.mutate(reason)}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}

function intervalLabel(t: ReturnType<typeof useT>, hours: number): string {
  if (hours % 168 === 0) return t('backups.interval.weeks', { count: hours / 168 });
  if (hours % 24 === 0) return t('backups.interval.days', { count: hours / 24 });
  return t('backups.interval.hours', { count: hours });
}

// ── History ─────────────────────────────────────────────────────────────────

function History() {
  const t = useT();
  const [status, setStatus] = useState<BackupRunStatus | ''>('');
  const [page, setPage] = useState(1);

  const runs = useQuery({
    queryKey: ['backups', 'runs', { status, page }],
    queryFn: () =>
      api.get<Paginated<BackupRun>>(
        '/api/backups/runs',
        compact({ status: status || undefined, page, limit: 20 }),
      ),
  });

  const columns: Column<BackupRun>[] = [
    {
      key: 'startedAt',
      header: t('backups.history.started'),
      render: (run) => (
        <div className="min-w-0">
          <div className="whitespace-nowrap text-ink">{formatDateTime(run.startedAt)}</div>
          <div className="truncate font-mono text-[11px] text-ink-3 md:max-w-[260px]" title={run.fileName}>
            {run.fileName}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('backups.history.status'),
      render: (run) => (
        <div className="space-y-1">
          <Badge tone={RUN_TONES[run.status]}>{t(`backups.status.${run.status}`)}</Badge>
          {run.error && (
            <p className="max-w-[320px] text-left text-xs text-danger break-anywhere">
              {run.error}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'trigger',
      header: t('backups.history.trigger'),
      render: (run) =>
        run.trigger === 'manual'
          ? t('backups.trigger.manualBy', { actor: run.actor ?? '—' })
          : t('backups.trigger.scheduled'),
    },
    {
      key: 'duration',
      header: t('backups.history.duration'),
      numeric: true,
      render: (run) => formatDuration(run.durationMs),
    },
    {
      key: 'size',
      header: t('backups.history.size'),
      numeric: true,
      render: (run) => formatBytes(run.sizeBytes),
    },
    {
      key: 'file',
      header: t('backups.history.file'),
      render: (run) =>
        run.fileUrl ? (
          <a
            href={run.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-brand hover:underline"
          >
            <ExternalLink size={13} />
            {t('backups.history.open')}
          </a>
        ) : run.prunedAt ? (
          <span className="text-ink-3" title={formatDateTime(run.prunedAt)}>
            {t('backups.history.pruned')}
          </span>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t('backups.history.title')}
        description={t('backups.history.description')}
        action={
          <div className="w-full sm:w-44">
            <Select
              aria-label={t('backups.history.status')}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as BackupRunStatus | '');
                setPage(1);
              }}
            >
              <option value="">{t('backups.history.all')}</option>
              {(Object.keys(RUN_TONES) as BackupRunStatus[]).map((value) => (
                <option key={value} value={value}>
                  {t(`backups.status.${value}`)}
                </option>
              ))}
            </Select>
          </div>
        }
      />
      {runs.isError && (
        <div className="p-4">
          <ErrorNote error={runs.error} />
        </div>
      )}
      {runs.isPending ? (
        <Skeleton className="m-4 h-48" />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={runs.data?.items ?? []}
            rowKey={(run) => run.id}
            empty={
              <EmptyState
                icon={<DatabaseBackup size={28} />}
                title={t('backups.history.emptyTitle')}
                description={t('backups.history.emptyDescription')}
              />
            }
          />
          {(runs.data?.total ?? 0) > 0 && (
            <Pagination
              page={runs.data?.page ?? 1}
              totalPages={runs.data?.totalPages ?? 1}
              total={runs.data?.total ?? 0}
              onChange={setPage}
            />
          )}
        </>
      )}
    </Card>
  );
}
